// src/composables/WeatherIcon.ts

type WeatherIconEntry = {
  day: string
  night?: string
  text: string
}

export default class WeatherIcon {
  private static resolve(file: string): string {
    return new URL(`../assets/images/weather/${file}`, import.meta.url).href
  }

  /**
   * 日別
   */
  static getDailyIconUrl(code?: number, isNight?: number): string {
    if (typeof code !== 'number') {
      return this.resolve('tenki01.png')
    }

    const entry = DAILY_ICON_MAP[code]
    if (!entry) return this.resolve('tenki01.png')

    if (isNight === 1 && entry.night) {
      return this.resolve(entry.night)
    }

    return this.resolve(entry.day)
  }

  static getDailyText(code?: number): string {
    return DAILY_ICON_MAP[code ?? -1]?.text ?? '不明'
  }

  /**
   * 時間別
   */
  static getHourlyIconUrl(code?: number, isNight?: number): string {
    if (typeof code !== 'number') {
      return this.resolve('tenki01.png')
    }

    const entry = HOURLY_ICON_MAP[code]
    if (!entry) return this.resolve('tenki01.png')

    if (isNight === 1 && entry.night) {
      return this.resolve(entry.night)
    }

    return this.resolve(entry.day)
  }

  static getHourlyText(code?: number): string {
    return HOURLY_ICON_MAP[code ?? -1]?.text ?? '不明'
  }
}

/* ============================
   1日用マップ
============================ */

const DAILY_ICON_MAP: Record<number, WeatherIconEntry> = {
  100: { day: 'tenkiG02.png', night: 'tenkiG04.png', text: '晴れ' },
  101: { day: 'tenkiG23.png', night: 'tenkiG26.png', text: '晴れ時々くもり' },
  102: { day: 'tenkiG82.png', night: 'tenkiG85.png', text: '晴れ一時雨' },
  103: { day: 'tenkiG24.png', night: 'tenkiG27.png', text: '晴れ時々雨' },
  104: { day: 'tenkiG25.png', night: 'tenkiG28.png', text: '晴れ一時雪' },
  105: { day: 'tenkiG25.png', night: 'tenkiG28.png', text: '晴れ時々雪' },
  107: { day: 'tenkiG41.png', night: 'tenkiG42.png', text: '晴れ時々雨か雪' },

  110: { day: 'tenkiG49.png', night: 'tenkiG52.png', text: '晴れ後時々くもり' },
  111: { day: 'tenkiG49.png', night: 'tenkiG52.png', text: '晴れ後くもり' },
  112: { day: 'tenkiG50.png', night: 'tenkiG53.png', text: '晴れ後一時雨' },
  113: { day: 'tenkiG50.png', night: 'tenkiG53.png', text: '晴れ後時々雨' },
  114: { day: 'tenkiG50.png', night: 'tenkiG53.png', text: '晴れ後雨' },
  115: { day: 'tenkiG51.png', night: 'tenkiG54.png', text: '晴れ後一時雪' },
  116: { day: 'tenkiG51.png', night: 'tenkiG54.png', text: '晴れ後時々雪' },
  117: { day: 'tenkiG51.png', night: 'tenkiG54.png', text: '晴れ後雪' },
  118: { day: 'tenkiG69.png', night: 'tenkiG70.png', text: '晴れ後雨か雪' },

  160: { day: 'tenkiG86.png', night: 'tenkiG87.png', text: '晴れ一時雪か雨' },
  170: { day: 'tenkiG44.png', night: 'tenkiG45.png', text: '晴れ時々雪か雨' },
  181: { day: 'tenkiG73.png', night: 'tenkiG74.png', text: '晴れ後雪か雨' },

  200: { day: 'tenkiG05.png', text: 'くもり' },
  201: { day: 'tenkiG29.png', night: 'tenkiG30.png', text: 'くもり時々晴れ' },
  202: { day: 'tenkiG83.png', text: 'くもり一時雨' },
  203: { day: 'tenkiG31.png', text: 'くもり時々雨' },
  204: { day: 'tenkiG32.png', text: 'くもり一時雪' },
  205: { day: 'tenkiG32.png', text: 'くもり時々雪' },
  207: { day: 'tenkiG43.png', text: 'くもり時々雨か雪' },

  210: { day: 'tenkiG55.png', night: 'tenkiG56.png', text: 'くもり後晴れ' },
  211: { day: 'tenkiG55.png', night: 'tenkiG56.png', text: 'くもり後晴れ' },
  212: { day: 'tenkiG57.png', text: 'くもり後一時雨' },
  213: { day: 'tenkiG57.png', text: 'くもり後時々雨' },
  214: { day: 'tenkiG57.png', text: 'くもり後雨' },
  215: { day: 'tenkiG58.png', text: 'くもり後一時雪' },
  216: { day: 'tenkiG58.png', text: 'くもり後時々雪' },
  217: { day: 'tenkiG58.png', text: 'くもり後雪' },
  218: { day: 'tenkiG71.png', text: 'くもり後雨か雪' },

  260: { day: 'tenkiG88.png', text: 'くもり一時雪か雨' },
  270: { day: 'tenkiG46.png', text: 'くもり時々雪か雨' },
  281: { day: 'tenkiG75.png', text: 'くもり後雪か雨' },

  300: { day: 'tenkiG10.png', text: '雨' },
  301: { day: 'tenkiG33.png', night: 'tenkiG34.png', text: '雨時々晴れ' },
  302: { day: 'tenkiG35.png', text: '雨時々止む' },
  303: { day: 'tenkiG36.png', text: '雨時々雪' },
  304: { day: 'tenkiG17.png', text: '雨か雪' },
  309: { day: 'tenkiG84.png', text: '雨一時雪' },

  311: { day: 'tenkiG59.png', night: 'tenkiG60.png', text: '雨後晴れ' },
  313: { day: 'tenkiG61.png', text: '雨後くもり' },
  314: { day: 'tenkiG62.png', text: '雨後時々雪' },
  315: { day: 'tenkiG62.png', text: '雨後雪' },
  316: { day: 'tenkiG76.png', night: 'tenkiG77.png', text: '雨か雪後晴れ' },
  317: { day: 'tenkiG78.png', text: '雨か雪後くもり' },

  340: { day: 'tenkiG18.png', text: '雪か雨' },
  361: { day: 'tenkiG79.png', night: 'tenkiG80.png', text: '雪か雨後晴れ' },
  371: { day: 'tenkiG81.png', text: '雪か雨後くもり' },

  400: { day: 'tenkiG14.png', text: '雪' },
  401: { day: 'tenkiG37.png', night: 'tenkiG38.png', text: '雪時々晴れ' },
  402: { day: 'tenkiG39.png', text: '雪時々止む' },
  403: { day: 'tenkiG40.png', text: '雪時々雨' },

  409: { day: 'tenkiG84.png', text: '雪一時雨' },

  411: { day: 'tenkiG64.png', night: 'tenkiG65.png', text: '雪後晴れ' },
  413: { day: 'tenkiG66.png', text: '雪後くもり' },
  414: { day: 'tenkiG67.png', text: '雪後雨' },
}

/* ============================
   時間別用マップ
============================ */

const HOURLY_ICON_MAP: Record<number, WeatherIconEntry> = {
  100: { day: 'tenkiG02.png', night: 'tenkiG04.png', text: '快晴' },
  101: { day: 'tenkiG01.png', night: 'tenkiG03.png', text: '晴れ' },

  200: { day: 'tenkiG06.png', text: '薄曇り' },
  201: { day: 'tenkiG05.png', text: '曇り' },

  300: { day: 'tenkiG09.png', text: '小雨' },
  301: { day: 'tenkiG07.png', text: '弱雨' },
  302: { day: 'tenkiG10.png', text: '雨' },
  303: { day: 'tenkiG11.png', text: '強雨' },
  304: { day: 'tenkiG12.png', text: '豪雨' },

  400: { day: 'tenkiG19.png', text: 'みぞれ' },

  500: { day: 'tenkiG16.png', text: '湿雪' },
  501: { day: 'tenkiG14.png', text: '乾雪' },
}
